import _ from 'lodash';
import { Listbox, Transition, Checkbox } from '@headlessui/react';
import { CaretDown, CaretUp, X, Check } from '@phosphor-icons/react';
import clsx from 'clsx';
import { Fragment, useRef, useState, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type Option = {
  value: string;
  label?: string;
};

export type RenderOptionContentArgs = {
  option: Option;
  isSelected: boolean;
};

type AutoCompleteMultiSelectBoxProps = {
  fetchOptions: (query: string) => Promise<void>;
  selectedOptions: Option[];
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onSelectionChange: (options: Option[]) => void;
  disabled?: boolean;
  direction?: 'up' | 'down';
  options: Option[];
  placeholder?: string;
  noOptionsText?: string;
  renderOptionContent?: (args: RenderOptionContentArgs) => ReactNode;
};

const variantClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 hover:opacity-90',
    secondary: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
    default: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
  },
  outlined: {
    primary: 'border hover:border-primary hover:text-primary',
    secondary:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
  text: {
    primary: 'text-primary hover:bg-primary hover:text-neutral-0',
    secondary: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
};

const activeClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 opacity-90',
    secondary: 'bg-neutral-500 text-neutral-0',
    default: 'bg-neutral-500 text-neutral-0',
  },
  outlined: {
    primary: 'border border-primary text-primary',
    secondary: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
    default: 'border border-neutral-400 text-neutral-0 bg-neutral-400',
  },
  text: {
    primary: 'bg-primary text-neutral-0',
    secondary: 'bg-neutral-400 text-neutral-0',
    default: 'bg-neutral-400 text-neutral-0',
  },
};

const sizeClasses = {
  small: 'px-2 py-1 text-sm leading-6',
  medium: 'px-3 py-2.5 text-base leading-6',
  large: 'px-4 py-3 text-lg leading-7',
};

export const AutoCompleteMultiSelectBox = ({
  fetchOptions,
  selectedOptions,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  className = '',
  onSelectionChange,
  disabled = false,
  direction = 'down',
  options,
  placeholder = 'Select values...',
  noOptionsText = 'No options available',
  renderOptionContent,
}: AutoCompleteMultiSelectBoxProps) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const maxHeight = 270; // Maximum height for dropdown (5 items * 54px)

  // Debounced fetchOptions
  const fetchOptionsRef = useRef(fetchOptions);
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  const debouncedFetchOptions = useRef(
    _.debounce(async (newQuery) => {
      setLoading(true);
      try {
        await fetchOptionsRef.current(newQuery);
      } catch (error) {
        console.error('Error fetching options:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
  );

  useEffect(() => {
    debouncedFetchOptions.current(query);
  }, [query]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const itemHeight = 54;
      const dropdownHeight =
        Math.min((options || []).length * itemHeight, maxHeight) + 10;
      const viewportHeight = window.innerHeight;
      const buttonHeight = rect.height;

      // Check if there's enough space below
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const padding = 8; // Padding is 4 on each side, totaling 8 for top and bottom

      // Check if dropdown would be cut off when going down
      const wouldBeCutOffDown =
        rect.top + buttonHeight + padding + dropdownHeight > viewportHeight;

      const shouldShowAbove =
        direction === 'up' ||
        (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) ||
        wouldBeCutOffDown;

      let top: number;

      const labelSize = 24;

      if (shouldShowAbove) {
        const calculatedTop =
          rect.top - dropdownHeight - labelSize + itemHeight;
        top = Math.max(padding, calculatedTop);
      } else {
        top = rect.top + buttonHeight + padding;
      }

      setPosition({
        top: top,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  const defaultRenderSelectedOption = (
    option: Option,
    onRemove: () => void,
  ) => (
    <span
      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      {option.label || option.value}
      <span
        role="button"
        tabIndex={0}
        aria-label={`Remove ${option.label || option.value}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }
        }}
        className="ml-1 text-primary-700 hover:text-primary-600 cursor-pointer focus:outline-none"
      >
        <X className="h-3 w-3" />
      </span>
    </span>
  );

  const renderOptionNode = (option: Option, isSelected: boolean) => {
    const handleSelect = () => handleSelection(option);
    const content = renderOptionContent
      ? renderOptionContent({
          option,
          isSelected,
        })
      : option.label || option.value;

    return (
      <>
        <Checkbox
          checked={isSelected}
          onChange={() => handleSelect()}
          className={clsx(
            'mr-3 h-4 w-4 rounded border-2',
            isSelected
              ? 'bg-primary border-primary'
              : 'bg-neutral-0 border-neutral-200',
          )}
        >
          {isSelected && (
            <span className="pointer-events-none absolute h-3 w-3 transform opacity-100 transition-opacity duration-200 ease-in-out">
              <Check className="h-3 w-3 text-neutral-0" />
            </span>
          )}
        </Checkbox>
        <span className="text-sm text-neutral-700 flex-1">{content}</span>
      </>
    );
  };

  const handleSelection = (option: Option) => {
    const isSelected = selectedOptions.some(
      (item) => item.value === option.value,
    );
    let updatedSelection;
    if (isSelected) {
      updatedSelection = selectedOptions.filter(
        (item) => item.value !== option.value,
      );
    } else {
      updatedSelection = [...selectedOptions, option];
    }
    onSelectionChange(updatedSelection);
  };

  const handleRemove = (value: string) => {
    const updated = selectedOptions.filter((item) => item.value !== value);
    onSelectionChange(updated);
  };

  const variantClass = variantClasses[variant][color];
  const sizeClass = sizeClasses[size];

  return (
    <Listbox
      value={selectedOptions}
      onChange={() => {}}
      multiple
      disabled={disabled}
    >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            ref={buttonRef}
            onClick={updatePosition}
            className={clsx(
              'w-full rounded-md border border-neutral-200',
              open ? activeClasses[variant][color] : variantClass,
              `${variantClass} ${sizeClass}`,
              'flex justify-between items-center leading-6',
              'focus:outline-none',
              'bg-neutral-0',
              className,
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <div className="flex-1 flex flex-wrap gap-1 items-center min-h-[1.5em]">
              {selectedOptions.length > 0 ? (
                selectedOptions.map((item) => {
                  const onRemove = () => handleRemove(item.value);
                  return (
                    <Fragment key={item.value}>
                      {defaultRenderSelectedOption(item, onRemove)}
                    </Fragment>
                  );
                })
              ) : (
                <i>{placeholder}</i>
              )}
            </div>
            {direction === 'up' ? (
              <CaretUp className="ml-2 flex-shrink-0" />
            ) : (
              <CaretDown className="ml-2 flex-shrink-0" />
            )}
          </Listbox.Button>

          <Transition
            as={'div'}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {createPortal(
              <Listbox.Options
                ref={listboxRef}
                className={clsx(
                  'absolute z-[9999] bg-neutral-0 shadow-lg rounded-md py-1',
                  'focus:outline-none text-sm',
                  'border border-neutral-100',
                )}
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  width: `${position.width}px`,
                  position: 'fixed',
                  maxHeight: `${maxHeight}px`,
                }}
              >
                <div className="flex justify-between items-center border-b border-neutral-100 sticky top-0 bg-neutral-0">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full px-3 py-2.5 focus:outline-none text-sm"
                  />
                  {query?.length > 0 && (
                    <button onClick={() => setQuery('')} className="px-3">
                      <X />
                    </button>
                  )}
                </div>
                <div className="overflow-auto block max-h-[200px]">
                  {loading ? (
                    <div className="text-center py-2.5 text-neutral-400">
                      Loading...
                    </div>
                  ) : (options ?? []).length > 0 ? (
                    options.map((option) => {
                      const isChecked = selectedOptions.some(
                        (item) => item.value === option.value,
                      );
                      return (
                        <Listbox.Option
                          key={option.value}
                          value={option}
                          className={({ active }) =>
                            clsx(
                              'cursor-pointer select-none relative py-3 px-4 m-2 rounded-lg truncate flex items-center',
                              active && 'bg-neutral-100',
                              isChecked && 'bg-neutral-100 font-bold',
                              'text-neutral-600',
                              'hover:bg-neutral-50',
                              'active:bg-neutral-100',
                            )
                          }
                          onClick={() => handleSelection(option)}
                        >
                          {renderOptionNode(option, isChecked)}
                        </Listbox.Option>
                      );
                    })
                  ) : (
                    <div className="text-center py-2.5 text-neutral-400">
                      {noOptionsText}
                    </div>
                  )}
                </div>
              </Listbox.Options>,
              document.body,
            )}
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
